"use client";

import Image from "next/image";
import { useButtonTracking } from "@/lib/useButtonTracking";
import { useState } from "react";
import {
  Heart,
  MessageSquare,
  Share2,
  Image as ImageIcon,
  Link as LinkIcon,
  Hash,
  Link2,
  Send,
} from "lucide-react";
import { Button } from "../ui/button";

interface CommunityProps {
  isMobile: boolean;
}

const leaderboardData = [
  {
    rank: 1,
    name: "Kai Wang",
    score: 1580,
    avatar: "https://api.dicebear.com/7.x/personas/svg?seed=Kai",
  },
  {
    rank: 2,
    name: "Valen Liu",
    score: 990,
    avatar: "https://api.dicebear.com/7.x/personas/svg?seed=Techie",
  },
  {
    rank: 3,
    name: "Lily Robbins",
    score: 845,
    avatar: "https://api.dicebear.com/7.x/personas/svg?seed=Lily",
  },
  {
    rank: 4,
    name: "Max J.",
    score: 720,
    avatar: "https://api.dicebear.com/7.x/personas/svg?seed=Max",
  },
  {
    rank: 5,
    name: "Giovanni Ferrero",
    score: 675,
    avatar: "https://api.dicebear.com/7.x/personas/svg?seed=Ferrero",
  },
];

const communityPosts = [
  {
    id: 1,
    author: "Kai Wang",
    avatar: "/2_community/kai wang.jpg",
    timeAgo: "3 days ago",
    title: "Codex Beyond Coding",
    content:
      // content-gate-ignore: verbatim member quote, their words not ours to restyle
      "Used Codex to clean up my Mac storage and 3x my network speed. Turns out coding agents are incredible at diagnosing real-world system issues — not just writing code.",
    likes: 12,
    comments: 5,
    shares: 3,
  },
  {
    id: 2,
    author: "Bocheng Yin",
    avatar: "/2_community/bocheng yin.jpeg",
    timeAgo: "2 days ago",
    title: "Could Agent Memory Save 40 Hours/Week for Your Team?",
    content:
      // content-gate-ignore: verbatim member quote, their words not ours to restyle
      "Ran a pilot eval on repo-visible agent memory — fresh sessions kept repeating the same mistakes. With persistent memory, 4/5 task families improved. Built ai-wiki-toolkit to make it real.",
    likes: 4,
    comments: 7,
    shares: 3,
  },
];

interface CommunityCardProps {
  profileImage: string;
  name: string;
  date: string;
  title: string;
  content: string;
  likes: number;
  comments: number;
  shares: number;
  onTrackInteraction?: (action: string) => void;
}

const CommunityCard: React.FC<CommunityCardProps> = ({
  profileImage,
  name,
  date,
  title,
  content,
  likes,
  comments,
  shares,
  onTrackInteraction,
}) => {
  // Handle tracking for various interactions
  const handleInteraction = (action: string) => {
    if (onTrackInteraction) {
      onTrackInteraction(action);
    }
  };

  return (
    <div
      className="bg-[#111111] border border-gray-800 p-5 transition-all duration-300 hover:bg-[#141414] rounded-lg mb-4"
      onClick={() => handleInteraction("view")}
    >
      <div className="flex items-center mb-4">
        <Image
          src={profileImage}
          alt={name}
          width={48}
          height={48}
          className="w-12 h-12 rounded-full mr-3"
          onClick={(e) => {
            e.stopPropagation();
            handleInteraction("profile_click");
          }}
        />
        <div>
          <h3 className="font-medium text-white">{name}</h3>
          <p className="text-xs text-white/50">{date}</p>
        </div>
      </div>

      <h4 className="font-semibold text-lg mb-3">{title}</h4>
      <p className="text-base text-white/70 mb-4">{content}</p>

      <div className="flex items-center text-sm text-white/50 space-x-5">
        <div
          className="flex items-center cursor-pointer hover:text-white/90 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            handleInteraction("like");
          }}
        >
          <Heart className="h-4 w-4 mr-2" />
          <span>{likes}</span>
        </div>
        <div
          className="flex items-center cursor-pointer hover:text-white/90 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            handleInteraction("comment");
          }}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          <span>{comments}</span>
        </div>
        <div
          className="flex items-center cursor-pointer hover:text-white/90 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            handleInteraction("share");
          }}
        >
          <Share2 className="h-4 w-4 mr-2" />
          <span>{shares}</span>
        </div>
      </div>
    </div>
  );
};

export default function Community({ isMobile }: CommunityProps) {
  const { trackButtonClick } = useButtonTracking();
  const [postInput, setPostInput] = useState("");
  const [activePostIndex, setActivePostIndex] = useState(0);

  const handleMediaButtonClick = (type: string) => {
    trackButtonClick(`community_${type}_button`, {
      section: "community",
      action: "media_upload",
      media_type: type,
    });
  };

  const handlePostClick = () => {
    trackButtonClick("community_post_button", {
      section: "community",
      action: "post_attempt",
    });
  };

  const handleLeaderboardClick = () => {
    trackButtonClick("community_see_all_leaderboard", {
      section: "community",
      action: "view_leaderboard",
    });
  };

  const nextPost = () => {
    setActivePostIndex((prev) => (prev + 1) % communityPosts.length);
  };

  const prevPost = () => {
    setActivePostIndex(
      (prev) => (prev - 1 + communityPosts.length) % communityPosts.length
    );
  };

  return (
    <section className="bg-black text-white pt-16">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h2 className={`${isMobile ? 'text-5xl' : 'text-6xl'} font-bold mb-6`}>Community of Builders</h2>
        <p className={`${isMobile ? 'text-xl' : 'text-2xl'} text-white/70 mb-8`}>
          Share what you're working on, get feedback, and connect with other AI builders
        </p>

        {/* Post creation UI */}
        <div className="bg-[#111111] border border-gray-800 p-4 mb-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <Image
              src="/1_hero/profiles/Frame 38.png"
              alt="Your profile"
              width={40}
              height={40}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex-1">
              <div
                className="bg-[#1a1a1a] hover:bg-[#222] rounded-full px-4 py-3 text-white/60 text-sm cursor-pointer transition-colors"
                onClick={() =>
                  trackButtonClick("community_create_post_input", {
                    action: "create_post_start",
                  })
                }
              >
                Share insights or Ask for help
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <div className="flex gap-4">
              <button
                className="flex items-center gap-2 text-white/60 hover:text-white/90 text-sm transition-colors"
                onClick={() => handleMediaButtonClick("image")}
              >
                <ImageIcon className="h-4 w-4" />
                <span>Image</span>
              </button>
              <button
                className="flex items-center gap-2 text-white/60 hover:text-white/90 text-sm transition-colors"
                onClick={() => handleMediaButtonClick("link")}
              >
                <LinkIcon className="h-4 w-4" />
                <span>Link</span>
              </button>
              <button
                className="flex items-center gap-2 text-white/60 hover:text-white/90 text-sm transition-colors"
                onClick={() => handleMediaButtonClick("topic")}
              >
                <Hash className="h-4 w-4" />
                <span>Topic</span>
              </button>
            </div>
            <button
              onClick={handlePostClick}
              className="bg-[#222] hover:bg-[#333] text-white/70 px-3 py-1 rounded-full text-sm transition-colors flex items-center"
            >
              <span>Post</span>
              <Send className="h-3 w-3 ml-2" />
            </button>
          </div>
        </div>

        {/* Main community content */}
        <div className={`${isMobile ? "flex flex-col" : "grid grid-cols-3 gap-8"}`}>
          <div className={`${isMobile ? "w-full" : "col-span-2"}`}>
            {isMobile ? (
              // Mobile view shows only one post at a time
              <div>
                <CommunityCard
                  key={communityPosts[activePostIndex].id}
                  profileImage={communityPosts[activePostIndex].avatar}
                  name={communityPosts[activePostIndex].author}
                  date={communityPosts[activePostIndex].timeAgo}
                  title={communityPosts[activePostIndex].title}
                  content={communityPosts[activePostIndex].content}
                  likes={communityPosts[activePostIndex].likes}
                  comments={communityPosts[activePostIndex].comments}
                  shares={communityPosts[activePostIndex].shares}
                  onTrackInteraction={(action) => {
                    trackButtonClick(`community_post_${action}`, {
                      section: "community",
                      action: action,
                      post_id: communityPosts[activePostIndex].id,
                    });
                  }}
                />

                <div className="flex justify-center mt-4 gap-2">
                  <button
                    onClick={prevPost}
                    className="p-2 bg-[#1a1a1a] rounded-full hover:bg-[#222] transition-colors"
                    aria-label="Previous post"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    onClick={nextPost}
                    className="p-2 bg-[#1a1a1a] rounded-full hover:bg-[#222] transition-colors"
                    aria-label="Next post"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              // Desktop view shows all posts
              <div className="space-y-4">
                {communityPosts.map((post) => (
                  <CommunityCard
                    key={post.id}
                    profileImage={post.avatar}
                    name={post.author}
                    date={post.timeAgo}
                    title={post.title}
                    content={post.content}
                    likes={post.likes}
                    comments={post.comments}
                    shares={post.shares}
                    onTrackInteraction={(action) => {
                      trackButtonClick(`community_post_${action}`, {
                        section: "community",
                        action: action,
                        post_id: post.id,
                      });
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Leaderboard */}
          <div className={`${isMobile ? "mt-8" : ""}`}>
            <div className="bg-[#111111] border border-gray-800 rounded-lg p-5">
              <h3 className="text-lg font-medium mb-4">Leaderboard (7 days)</h3>
              <div className="space-y-3">
                {leaderboardData.map((user) => (
                  <div
                    key={user.rank}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-white/60 w-5">{user.rank}</span>
                      <Image
                        src={user.avatar}
                        alt={user.name}
                        width={24}
                        height={24}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-sm">{user.name}</span>
                    </div>
                    <span className="bg-[#1a1a1a] text-white/70 px-2 py-1 rounded text-xs">
                      {user.score}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 text-center">
                <button
                  onClick={handleLeaderboardClick}
                  className="text-white/60 hover:text-white/90 text-sm transition-colors flex items-center justify-center w-full"
                >
                  <span>See all leaderboard</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="ml-1"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
